import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle, Rect } from 'react-native-svg';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationsApi, organizationalUnitsApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';

import { API_BASE_URL } from '../../constants/config';
import ConfirmModal from '../../components/ConfirmModal';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import { useNotification } from '../../contexts/NotificationContext';
import { Organization, OrganizationalUnit } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, EmptyState, PageContainer, Navbar, Breadcrumb } from '../../components';
import { DashboardSkeleton } from '../../components/Skeleton';
import OrganizationalChart from '../../components/OrgChart';
import { useAuth } from '../../contexts/AuthContext';
import { isOrgExecutive } from '../../utils/userPermissions';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { subscribe as subscribeInvalidation } from '../../utils/dataInvalidation';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Extended unit type with dashboard fields
interface DepartmentUnit extends OrganizationalUnit {
  description?: string;
  children_count?: number;
  goals_count?: number;
  kpis_count?: number;
  processes_count?: number;
  employees_count?: number;
  achievement_percentage?: number;
}

// Icons as SVG components
const EditIcon = () => (
  <Svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <Path
      d="M10.625 1.875L13.125 4.375M1.25 13.75L1.875 10.9375L10.9375 1.875L13.125 4.0625L4.0625 13.125L1.25 13.75Z"
      stroke="#64748B"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const DepartmentIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Rect x="10" y="3.33" width="20" height="33.33" rx="2" stroke="#414042" strokeWidth="1.5" />
    <Rect x="3.33" y="20" width="6.67" height="16.67" rx="1" stroke="#414042" strokeWidth="1.5" />
    <Rect x="30" y="15" width="6.67" height="21.67" rx="1" stroke="#414042" strokeWidth="1.5" />
    <Path d="M16.67 10H23.33M16.67 16.67H23.33M16.67 23.33H23.33M16.67 30H23.33" stroke="#414042" strokeWidth="1.5" strokeLinecap="round" />
  </Svg>
);

const SectionsIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Rect x="10" y="3.33" width="20" height="33.33" rx="2" stroke="#414042" strokeWidth="1.5" />
    <Rect x="3.33" y="20" width="6.67" height="16.67" rx="1" stroke="#414042" strokeWidth="1.5" />
    <Rect x="30" y="15" width="6.67" height="21.67" rx="1" stroke="#414042" strokeWidth="1.5" />
    <Path d="M16.67 10H23.33M16.67 16.67H23.33M16.67 23.33H23.33M16.67 30H23.33" stroke="#414042" strokeWidth="1.5" strokeLinecap="round" />
  </Svg>
);

const EmployeesIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Path
      d="M28.33 35V31.67C28.33 29.9 27.63 28.20 26.38 26.95C25.13 25.70 23.43 25 21.67 25H8.33C6.57 25 4.87 25.70 3.62 26.95C2.37 28.20 1.67 29.9 1.67 31.67V35"
      stroke="#414042"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M15 18.33C18.68 18.33 21.67 15.35 21.67 11.67C21.67 7.98 18.68 5 15 5C11.32 5 8.33 7.98 8.33 11.67C8.33 15.35 11.32 18.33 15 18.33Z"
      stroke="#414042"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M38.33 35V31.67C38.33 30.19 37.84 28.76 36.94 27.59C36.03 26.42 34.76 25.59 33.33 25.22"
      stroke="#414042"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M26.67 5.22C28.11 5.58 29.38 6.42 30.29 7.59C31.20 8.76 31.68 10.19 31.68 11.67C31.68 13.16 31.20 14.58 30.29 15.75C29.38 16.93 28.11 17.77 26.67 18.13"
      stroke="#414042"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const PlusIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M8 3.33333V12.6667M3.33333 8H12.6667"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const SquarePlusIcon = () => (
  <Svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <Path
      d="M7 4.375V9.625M4.375 7H9.625"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M1.75 3.5C1.75 2.53 2.53 1.75 3.5 1.75H10.5C11.47 1.75 12.25 2.53 12.25 3.5V10.5C12.25 11.47 11.47 12.25 10.5 12.25H3.5C2.53 12.25 1.75 11.47 1.75 10.5V3.5Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ArrowLeftIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M10 12L6 8L10 4"
      stroke="#737373"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const TrashIcon = ({ color = '#EF4444' }: { color?: string }) => (
  <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 6V20C19 21.1 18.1 22 17 22H7C5.9 22 5 21.1 5 20V6M8 6V4C8 2.9 8.9 2 10 2H14C15.1 2 16 2.9 16 4V6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Circular Progress Component - 80x80 with dynamic color based on achievement
const CircularProgress = ({ percentage, size = 80, strokeWidth = 6 }: { percentage: number; size?: number; strokeWidth?: number }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  let progressColor = '#EF4444'; // Red - Needs Improvement (< 80%)
  if (percentage >= 100) progressColor = '#22C55E'; // Green - Excellent
  else if (percentage >= 80) progressColor = '#F97316'; // Orange - Good

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#E5E5E5"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <Text style={styles.progressText}>%{percentage}</Text>
    </View>
  );
};

// Stat Row Component for department cards - Figma style (label pill on right, value on left)
const StatRow = ({ label, value }: { label: string; value: number | string }) => (
  <View style={styles.statRowContainer}>
    <Text style={styles.statRowValue}>{value}</Text>
    <View style={styles.statLabelPill}>
      <Text style={styles.statLabelText}>{label}:</Text>
    </View>
  </View>
);

export default function MyOrganizationScreen() {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<any>();
  const { navigateToHome, canAdd, canEdit, canDelete } = useRoleNavigation();
  // DTS 2.2 — "إدارة أعضاء الفريق وتوزيع المهام": only Section Head /
  // superuser can restructure the org tree. The coarse canEdit /
  // canDelete from useRoleNavigation is true for every org_employee
  // (data-entry open), which would let plain Employees see the pencil
  // and per-card Edit/Delete on /my-organization. Gate on the real
  // permission instead.
  const canManageTeam = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.team.manage')
  );
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardWidth } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedYear } = useYear();
  const { translate, translateOrg, translateUnit } = useAutoTranslate();
  const scrollViewRef = useRef<ScrollView>(null);
  const unitsSectionY = useRef<number>(0);

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const orgId = user?.profile?.organization;

  // Strategy Team / superuser see the whole authority through this screen
  // (directorate request 2026-05-12). They reach this view via the
  // "جهتي" drawer entry — same screen as a Director, but global scope.
  //
  // Two subtleties this flag covers:
  //   1. strat@hr.ae locally has no department_id in SSO seed data, so
  //      orgId is null. Without this branch the screen errors out with
  //      "لا توجد جهة مرتبطة" before any query fires.
  //   2. Even on staging where strat@hr.ae HAS a department, the user
  //      still wants the global tree — not just their section.
  const isGlobalScope = !!user && (
    user.is_superuser
    || (user.permissions || []).includes('hrcpms.strategic_goals.create')
  );

  // Deletion state - simple confirmation popup before delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState<DepartmentUnit | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Org structure file state
  const [orgStructureFile, setOrgStructureFile] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [orgStructureAspectRatio, setOrgStructureAspectRatio] = useState<number>(16 / 9);
  const [orgStructureZoom, setOrgStructureZoom] = useState(0.8);

  const isOrgEmployee = user?.position_type === 'EMPLOYEE';

  const handleEditDept = (dept: DepartmentUnit) => {
    navigation.navigate('AddOrganizationalUnit', {
      unitId: dept.id,
      parentId: dept.parent ?? undefined,
      parentName: dept.parent_name ?? undefined,
    });
  };

  const handleDeleteDept = (dept: DepartmentUnit) => {
    setDeptToDelete(dept);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDept = async () => {
    if (!deptToDelete) return;
    setIsDeleting(true);
    try {
      const response = await organizationalUnitsApi.delete(deptToDelete.id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        fetchData();
      } else {
        showSuccess(t('myOrganization.deleteSuccess'));
        // Invalidate + refetch — axios interceptor will also fire but this
        // makes the delete feel instant.
        fetchData();
      }
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        fetchData();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('myOrganization.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeptToDelete(null);
    }
  };

  const getFullMediaUrl = (path: string | null) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    // API_BASE_URL is like http://localhost:8002/api/v1 — extract origin
    const origin = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
    return `${origin}${path}`;
  };

  const handleFileSelected = async (file: File) => {
    const targetId = orgId || rootDeptId;
    if (!targetId) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const response = await organizationsApi.uploadOrgStructure(targetId, formData);
      setOrgStructureFile(getFullMediaUrl(response.org_structure_file));
      showSuccess(t('orgChart.uploadSuccess'));
    } catch (err) {
      console.error('Error uploading org structure:', err);
      showError(t('orgChart.uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteOrgStructureFile = async () => {
    const targetId = orgId || rootDeptId;
    if (!targetId) return;
    try {
      await organizationsApi.deleteOrgStructure(targetId);
      setOrgStructureFile(null);
      showSuccess(t('orgChart.deleteSuccess'));
    } catch (err) {
      console.error('Error deleting org structure file:', err);
      showError(t('orgChart.deleteFailed'));
    }
  };


  // Measure uploaded image aspect ratio for proper scaling
  useEffect(() => {
    if (orgStructureFile) {
      Image.getSize(orgStructureFile, (width, height) => {
        if (width && height) setOrgStructureAspectRatio(width / height);
      }, () => {});
    }
  }, [orgStructureFile]);

  // Responsive card width for department cards
  const departmentCardWidth = cardWidth.third;

  // Organizational units — full DHR tree for visualization.
  // `structure_only=true` lets any authenticated user see the whole hierarchy
  // for the org-chart view; entering individual unit details still goes
  // through has_object_permission, so a Section Head can SEE other sections
  // exist but can't drill into their data (per DTS scoping).
  //
  // Fired BEFORE the organization query so that Strategy Team users
  // without an orgId can derive a root department_id from the unit list
  // and feed it into effectiveOrgId below.
  const {
    data: unitsResponse,
    isLoading: unitsLoading,
    refetch: refetchUnits,
  } = useQuery({
    queryKey: [...queryKeys.organizationalUnits.list({ structure_only: true }), selectedYear],
    queryFn: () =>
      organizationalUnitsApi.list({
        structure_only: true,
        page_size: 100,
      }),
    enabled: !!orgId || isGlobalScope,
    staleTime: STALE_TIMES.ORGANIZATIONS,
  });

  const allUnits: DepartmentUnit[] = useMemo(
    () => unitsResponse?.results || [],
    [unitsResponse],
  );

  // For Strategy Team users WITHOUT an orgId in SSO (local seed gap), we
  // wait for the units query to land, pick the topmost unit's
  // department_id, and use that as the implicit anchor. The organization
  // fetch below stays disabled until then.
  const rootDeptId = useMemo<number | null>(() => {
    if (orgId) return null;  // explicit org wins — no derivation needed
    if (!isGlobalScope) return null;
    if (allUnits.length === 0) return null;
    // "Topmost" means an actual tree root — a unit with no parent. `parent`
    // is always present in the payload, so this holds even if `level` is
    // missing; relying on `level` alone silently degraded to raw id order
    // when the serializer didn't emit it, which anchored the whole screen on
    // whichever authority owned the lowest id (2026-08-17).
    const roots = allUnits.filter((u: any) => u.parent == null);
    const candidates = roots.length > 0 ? roots : allUnits;
    const sorted = [...candidates].sort((a: any, b: any) => {
      const la = a.level ?? 999;
      const lb = b.level ?? 999;
      if (la !== lb) return la - lb;
      // Tiebreak: smallest id (deterministic across reloads)
      return a.id - b.id;
    });
    return (sorted[0] as any)?.department_id || null;
  }, [orgId, isGlobalScope, allUnits]);

  const effectiveOrgId = orgId || rootDeptId;

  const {
    data: organization,
    isLoading: organizationLoading,
    refetch: refetchOrganization,
  } = useQuery<any>({
    queryKey: [...queryKeys.organizations.detail(effectiveOrgId || 0), 'myorg', selectedYear],
    queryFn: async () => {
      const raw = await organizationsApi.get(effectiveOrgId!);
      return (raw as any)?.organization ?? raw;
    },
    enabled: !!effectiveOrgId,
    staleTime: STALE_TIMES.ORGANIZATIONS,
  });

  // Determine which units the current user can drill into (the "green" scope).
  // Rule:
  //   - Strategy Team (has hrcpms.strategic_goals.create) → all units
  //   - Anyone else → their anchor unit (department_id == user.organization.id)
  //                   plus every descendant of it in the org tree
  // This means:
  //   - DG (anchor = DHR root) → all units
  //   - Director (anchor = directorate) → directorate + its sections
  //   - Section Head / Employee (anchor = section) → just that section
  const userScopeIds = useMemo<Set<number>>(() => {
    const result = new Set<number>();
    if (!user || !allUnits.length) return result;

    const isStrategyTeam = (user.permissions || []).includes('hrcpms.strategic_goals.create');
    if (user.is_superuser || isStrategyTeam) {
      allUnits.forEach(d => result.add(d.id));
      return result;
    }

    const userDeptId = user.organization?.id;
    if (!userDeptId) return result;

    const anchor = allUnits.find(d => (d as any).department_id === userDeptId);
    if (!anchor) return result;

    // BFS down the tree from the anchor unit.
    const childrenByParent = new Map<number, DepartmentUnit[]>();
    allUnits.forEach(d => {
      const pid = (d as any).parent;
      if (pid != null) {
        const arr = childrenByParent.get(pid) || [];
        arr.push(d);
        childrenByParent.set(pid, arr);
      }
    });

    const queue: number[] = [anchor.id];
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (result.has(id)) continue;
      result.add(id);
      const kids = childrenByParent.get(id) || [];
      kids.forEach(k => queue.push(k.id));
    }
    return result;
  }, [user, allUnits]);

  // Cards section shows ONLY the units the user can drill into (their scope).
  // The full org chart above already gives them visual context for the rest
  // of the entity, so we don't repeat out-of-scope units as cards.
  // Sort the user's own unit (the "قسمي" anchor) to the front so it's always
  // the first card; the rest keep their original tree order.
  const departments: DepartmentUnit[] = useMemo(() => {
    const inScope = allUnits.filter(u => userScopeIds.has(u.id));
    const userDeptId = user?.organization?.id;
    if (!userDeptId) return inScope;
    return [...inScope].sort((a, b) => {
      const aAnchor = (a as any).department_id === userDeptId ? 0 : 1;
      const bAnchor = (b as any).department_id === userDeptId ? 0 : 1;
      return aAnchor - bAnchor;
    });
  }, [allUnits, userScopeIds, user]);
  const totalEmployeesCount = organization?.actual_employee_count || 0;
  // Loading covers three cases:
  //   1. User is still loading from AuthContext (user === undefined/null)
  //   2. User loaded, orgId resolved, queries still fetching
  // Only fall through to the "no organization" error when user is known-loaded
  // AND they have no organization assigned.
  const userReady = !!user;
  const loading =
    !userReady
    || ((!!orgId || isGlobalScope) && (organizationLoading || unitsLoading));

  // Surface the "no organization assigned" state only after user has loaded.
  // Strategy Team / superuser see the global view even if their SSO record
  // lacks a department_id — they reach this screen specifically for the
  // authority-wide picture, so the error state is wrong for them.
  useEffect(() => {
    if (!userReady) {
      setError(null);
      return;
    }
    if (!orgId && !isGlobalScope) {
      setError(t('myOrganization.noOrganizationAssigned'));
    } else {
      setError(null);
    }
  }, [userReady, orgId, isGlobalScope, t]);

  // Keep the org-structure file state in sync with the query result. This
  // preserves the existing upload/delete handlers that still mutate local
  // state directly.
  useEffect(() => {
    if (organization) {
      setOrgStructureFile(getFullMediaUrl(organization.org_structure_file) || null);
    }
  }, [organization]);

  // Back-compat shim for mutation handlers that still call fetchData().
  // Invalidates both organization + units so the next access refetches.
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.organizationalUnits.all });
  }, [queryClient]);

  // Cross-screen invalidation: mutation screens can still call
  // invalidate('my-organization') for explicit refreshes. React Query cache
  // is also invalidated automatically by api.ts's response interceptor on
  // any POST/PATCH/DELETE — this is a belt-and-braces hook.
  useEffect(() => {
    return subscribeInvalidation('my-organization', () => {
      fetchData();
    });
  }, [fetchData]);

  // Scroll to units section when navigated with scrollToUnits param
  useEffect(() => {
    if (route.params?.scrollToUnits && unitsSectionY.current > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: unitsSectionY.current, animated: true });
      }, 300);
    }
  }, [route.params?.scrollToUnits, loading]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refetchOrganization(), refetchUnits()]);
    } finally {
      setRefreshing(false);
    }
  }, [refetchOrganization, refetchUnits]);

  // Calculate totals for stats from real data
  const totalDepartments = departments.length;
  const totalSections = departments.reduce((sum, d) => sum + (d.children_count || 0), 0);
  const totalEmployees = departments.reduce((sum, d) => sum + (d.employees_count || 0), 0);
  // The الأقسام tile only makes sense when the user owns a whole department:
  // a department contains sections, so its section count is meaningful. A
  // section-head's scope is a single section (no sub-sections), so the count
  // is always 0 — hide the tile for them rather than show a confusing 0.
  const hasDepartmentInScope = departments.some(d => d.unit_type === 'department');

  // Full-page skeleton only on cold load (no cached organization yet).
  // Warm cache revisits and background refetches fall through to the
  // regular render so the page feels instant. Matches GoalsScreen /
  // UnitGoalsScreen / etc.
  if (loading && !organization) {
    return (
      <PageContainer>
        <Navbar />
        <DashboardSkeleton />
      </PageContainer>
    );
  }

  if (error || !organization) {
    return (
      <PageContainer>
        <Navbar />
        <EmptyState
          icon="business-outline"
          title={t('navigation.myOrganization')}
          message={error || t('myOrganization.notFound')}
          actionLabel={t('common.refresh')}
          onAction={fetchData}
        />
      </PageContainer>
    );
  }


  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('navigation.myOrganization') },
          ]}
        />

        {/* Organization Header Card */}
        {/* Department name + description are owned by SSO (edited by the SSO
            superuser in the SSO "edit department" screen) and are read-only
            here — no edit affordance. */}
        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isMobile && { flexDirection: 'column', gap: 12 }, { justifyContent: 'flex-end' }]}>
          <View style={styles.headerContent}>
            <Text style={[styles.orgName, { color: themeColors.textPrimary }]}>{translateOrg(organization.name)}</Text>
            <Text style={[styles.orgDescription, { color: themeColors.textMuted }]}>
              {translate(organization.description) || t('myOrganization.noDescription')}
            </Text>
          </View>
        </View>

        {/* Stats Row - Order: الإدارات, الأقسام, الموظفين (RTL) */}
        <View style={[
          styles.statsRow,
          { flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 20 }
        ]}>
          <TouchableOpacity
            style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            // الموظف (Section Employee) cannot open the employees directory —
            // show the headcount but make the card non-navigable for them.
            disabled={isOrgEmployee}
            onPress={isOrgEmployee ? undefined : () => navigation.getParent()?.navigate('EmployeesStack', {
              screen: 'Employees',
              params: { organizationId: effectiveOrgId, organizationName: organization?.name },
            })}
            activeOpacity={isOrgEmployee ? 1 : 0.7}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardLabel, { color: themeColors.textPrimary }]}>{t('myOrganization.employees')}</Text>
              <View style={styles.statIconContainer}>
                <EmployeesIcon />
              </View>
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{totalEmployeesCount}</Text>
          </TouchableOpacity>
          {hasDepartmentInScope && (
            <TouchableOpacity
              style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => navigation.navigate('OrganizationalUnitDetail' as any, { id: departments[0]?.id })}
              activeOpacity={0.7}
              disabled={departments.length === 0}
            >
              <View style={styles.statCardHeader}>
                <Text style={[styles.statCardLabel, { color: themeColors.textPrimary }]}>{t('myOrganization.sections')}</Text>
                <View style={styles.statIconContainer}>
                  <SectionsIcon />
                </View>
              </View>
              <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{totalSections}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.statCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={() => departments.length > 0 && navigation.navigate('OrganizationalUnitDetail' as any, { id: departments[0]?.id })}
            activeOpacity={0.7}
            disabled={departments.length === 0}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardLabel, { color: themeColors.textPrimary }]}>{t('myOrganization.departments')}</Text>
              <View style={styles.statIconContainer}>
                <DepartmentIcon />
              </View>
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{totalDepartments}</Text>
          </TouchableOpacity>
        </View>

        {/* Organizational Chart Section — الهيكل التنظيمي للدائرة, drawn from
            SSO and read-only (manual upload + edit/color controls removed). */}
        <View style={styles.chartSection} onLayout={(e) => { unitsSectionY.current = e.nativeEvent.layout.y; }}>
          <View style={[styles.orgStructureHeader, { marginBottom: 12 }]}>
            <Text style={[styles.orgStructureTitle, { color: themeColors.textPrimary }]}>{t('orgChart.orgStructure')}</Text>
          </View>
          <OrganizationalChart
            organizationId={user?.profile?.organization}
            organizationName={translateOrg(organization.name)}
            inScopeIds={userScopeIds}
            onNodePress={(node) => {
              navigation.navigate('OrganizationalUnitDetail', { id: node.id });
            }}
            onMoveComplete={fetchData}
          />
        </View>

        {/* Section Title Card (Add button removed — adding units is disabled) */}
        <View style={[styles.sectionTitleCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isMobile && { flexDirection: 'column', gap: 12, alignItems: 'stretch' }, { justifyContent: 'flex-end' }]}>
          <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('sidebar.organizationalUnits')}</Text>
        </View>

        {/* Departments Grid */}
        {departments.length > 0 ? (
          <View style={[styles.departmentsGrid, { gap: isMobile ? 12 : 16 }]}>
            {departments.map((dept: DepartmentUnit, index: number) => {
              // departments is already filtered to in-scope units, so every
              // card here is interactive. The anchor unit (user's own dept)
              // gets a "قسمي" badge so they can quickly tell theirs apart
              // when several scope-units are visible (Director / DG view).
              const isAnchor = !!user?.organization?.id
                && (dept as any).department_id === user.organization.id;
              return (
              <View
                key={dept.id || index}
                style={[
                  styles.departmentCard,
                  {
                    width: departmentCardWidth,
                    minWidth: isMobile ? undefined : 300,
                    backgroundColor: isAnchor ? '#F0FDF4' : themeColors.card,
                    borderColor: isAnchor ? '#22C55E' : themeColors.cardBorder,
                    borderWidth: isAnchor ? 2 : 1,
                  },
                ]}
              >
                {/* Main Content Row */}
                <View style={styles.cardContent}>
                  {/* Left side - Progress Circle */}
                  <View style={styles.progressContainer}>
                    <CircularProgress
                      percentage={dept.achievement_percentage || 0}
                    />
                  </View>

                  {/* Right side - Info */}
                  <View style={styles.cardInfo}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <Text style={[styles.departmentName, { color: themeColors.textPrimary }]}>{translateUnit(dept.name_ar || dept.name)}</Text>
                      {isAnchor && (
                        <View style={{ backgroundColor: '#22C55E', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 }}>
                          <Text style={{ color: '#FFFFFF', fontSize: 11, fontFamily: FONTS.regular }}>قسمي</Text>
                        </View>
                      )}
                      {(dept as any).has_pending_deletion && <PendingDeletionBadge compact />}
                    </View>
                    <Text style={[styles.departmentDescription, { color: themeColors.textMuted }]} numberOfLines={2}>
                      {translate(dept.description) || '-'}
                    </Text>

                    {/* Stats List - Vertical layout matching Figma */}
                    <View style={styles.statsList}>
                      <StatRow label={t('myOrganization.sections')} value={dept.children_count ?? 0} />
                      <StatRow label={t('common.kpis')} value={dept.kpis_count ?? 0} />
                      <StatRow label={t('navigation.processes')} value={dept.processes_count ?? 0} />
                      <StatRow label={t('myOrganization.employees')} value={dept.employees_count ?? 0} />
                    </View>
                  </View>
                </View>

                {/* Footer - every visible card is in-scope (filtered above)
                    so all are clickable for full details. */}
                <View style={styles.cardFooter}>
                  <TouchableOpacity
                    style={[styles.viewDetailsButton, { minHeight: 44, flex: 1 }]}
                    onPress={() => navigation.navigate('OrganizationalUnitDetail', { id: dept.id })}
                  >
                    <ArrowLeftIcon />
                    <Text style={[styles.viewDetailsText, { color: themeColors.textMuted }]}>{t('common.viewDetails')}</Text>
                  </TouchableOpacity>
                  {canManageTeam && (
                    <View style={styles.cardActions}>
                      <TouchableOpacity style={styles.cardActionBtn} onPress={() => handleEditDept(dept)}>
                        <EditIcon />
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.cardActionBtn} onPress={() => handleDeleteDept(dept)}>
                        <TrashIcon color="#EF4444" />
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              </View>
              );
            })}
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>{t('myOrganization.noDepartments')}</Text>
            <Text style={[styles.emptySubText, { color: themeColors.textMuted }]}>{t('myOrganization.addDepartmentHint')}</Text>
          </View>
        )}

      </ScrollView>

      {/* Delete confirmation popup */}
      <ConfirmModal
        visible={showDeleteConfirm}
        type="danger"
        title={t('common.confirmDelete')}
        message={`${t('myOrganization.confirmDeleteDepartment')} "${deptToDelete?.name_ar || deptToDelete?.name || ''}"؟`}
        confirmText={isOrgEmployee ? t('deletion.submitRequest') : t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDeleteDept}
        onCancel={() => { setShowDeleteConfirm(false); setDeptToDelete(null); }}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbText: {
    fontSize: 14,
    color: '#737373',
    fontFamily: FONTS.regular,
  },
  breadcrumbSeparator: {
    fontSize: 14,
    color: '#D4D4D4',
    fontFamily: FONTS.regular,
  },
  breadcrumbActive: {
    fontSize: 14,
    color: '#A9211F',
    fontFamily: FONTS.medium,
  },
  // Header Card
  headerCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    flexDirection: 'row',
    alignItems: 'center',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  editButton: {
    width: 44,
    height: 44,
    minWidth: 44,
    minHeight: 44,
    borderRadius: 8,
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerContent: {
    flex: 1,
    alignItems: 'flex-end',
    marginRight: 8,
    gap: 8,
  },
  orgName: {
    fontSize: 20,
    color: '#414042',
    textAlign: 'right',
    fontFamily: FONTS.medium,
  },
  orgDescription: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 24,
    fontFamily: FONTS.regular,
  },
  // Stats Row
  statsRow: {
    marginBottom: 16,
  },
  statCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  statCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16,
  },
  statIconContainer: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statCardLabel: {
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    flex: 1,
    fontFamily: FONTS.regular,
  },
  statCardValue: {
    fontSize: 30,
    color: '#414042',
    textAlign: 'center',
    lineHeight: 40,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  // Organizational Structure Card
  orgStructureCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  orgStructureHeader: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  orgStructureTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    textAlign: 'right',
  },
  orgStructureActions: {
    flexDirection: 'row-reverse',
    gap: 8,
  },
  orgStructureActionBtn: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  orgStructureActionBtnText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontFamily: FONTS.medium,
  },
  orgStructureEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderStyle: 'dashed',
    borderRadius: 8,
    gap: 12,
  },
  orgStructureEmptyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
  },
  orgStructureHintText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    opacity: 0.7,
  },
  orgStructureZoomControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  orgStructureZoomBtn: {
    padding: 6,
    borderRadius: 6,
  },
  orgStructureZoomText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    minWidth: 36,
    textAlign: 'center',
  },
  orgStructureImageWrapper: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  orgStructureImage: {
    width: '100%',
    borderRadius: 8,
  },
  // Organizational Chart Section
  chartSection: {
    marginBottom: 24,
  },
  // Section Title Card with Add Button
  sectionTitleCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    minHeight: 44,
    gap: 6,
  },
  addButtonText: {
    fontSize: 14,
    color: '#F8FAFC',
    fontFamily: FONTS.medium,
  },
  sectionTitle: {
    fontSize: 20,
    color: '#414042',
    textAlign: 'right',
    fontFamily: FONTS.medium,
  },
  // Departments Grid
  departmentsGrid: {
    // RTL: lay cards out right→left so the first card (قسمي) sits at the far
    // right and subsequent cards fill toward the left.
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
  },
  departmentCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: 16,
    overflow: 'hidden',
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  progressContainer: {
    // Progress circle container
  },
  progressText: {
    position: 'absolute',
    fontSize: 16,
    color: '#414042',
    fontFamily: FONTS.medium,
  },
  cardInfo: {
    flex: 1,
    alignItems: 'flex-end',
    gap: 10,
  },
  departmentName: {
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    fontFamily: FONTS.medium,
  },
  departmentDescription: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 14,
    fontFamily: FONTS.regular,
  },
  // Stats List - Vertical layout matching Figma
  statsList: {
    width: '100%',
    gap: 0,
  },
  // Stat Row - Label pill on right, value on left
  statRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    width: '100%',
  },
  statRowValue: {
    fontSize: 12,
    color: '#414042',
    flex: 1,
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  statLabelPill: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    width: 80,
    alignItems: 'flex-end',
  },
  statLabelText: {
    fontSize: 12,
    color: '#414042',
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  // View Details
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 8,
    minHeight: 44,
    borderRadius: 4,
  },
  viewDetailsText: {
    fontSize: 12,
    color: '#414042',
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  // Card footer row
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    paddingTop: 4,
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  cardActionBtn: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
  },
  // Delete Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    width: 340,
    borderRadius: 12,
    padding: 24,
    gap: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    textAlign: 'right',
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: 14,
    color: '#374151',
    fontFamily: FONTS.medium,
  },
  modalDeleteButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#EF4444',
    alignItems: 'center',
  },
  modalButtonDisabled: {
    opacity: 0.6,
  },
  modalDeleteButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontFamily: FONTS.medium,
  },
  // Empty State
  emptyContainer: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 40,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#414042',
    textAlign: 'center',
    marginBottom: 8,
    fontFamily: FONTS.medium,
  },
  emptySubText: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
});
